import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Theme } from '../../theme';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(247, 147, 26, 0.3); }
  50% { box-shadow: 0 0 20px rgba(247, 147, 26, 0.5); }
`;

const Container = styled.div<{ $x: number; $y: number; $theme: Theme; $isDragging: boolean }>`
  position: absolute;
  left: ${p => p.$x}px;
  top: ${p => p.$y}px;
  z-index: 200;
  pointer-events: auto;
  cursor: ${p => p.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  transition: ${p => p.$isDragging ? 'none' : 'box-shadow 0.2s'};
  
  @media (max-width: 768px) {
    position: fixed;
    left: 50% !important;
    top: auto !important;
    bottom: 100px;
    transform: translateX(-50%);
  }
`;

const Card = styled.div<{ $theme: Theme }>`
  background: ${p => p.$theme.surface};
  border: 2px solid ${p => p.$theme.primary};
  border-radius: 12px;
  padding: 14px 18px;
  animation: ${pulse} 2.5s ease-in-out infinite;
  min-width: 240px;
  max-width: 280px;
  
  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 320px;
    padding: 16px 20px;
  }
`;

const Header = styled.div<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${p => p.$theme.border};
`;

const Title = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.primary};
  font-size: 14px;
  font-weight: 700;
`;

const CloseBtn = styled.button<{ $theme: Theme }>`
  background: none;
  border: none;
  color: ${p => p.$theme.text};
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  opacity: 0.6;
  line-height: 1;
  
  &:hover {
    opacity: 1;
  }
`;

const Subtitle = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 13px;
  opacity: 0.8;
  margin-bottom: 10px;
`;

const SocialProof = styled.div<{ $theme: Theme }>`
  background: linear-gradient(90deg, rgba(76, 175, 80, 0.15), rgba(247, 147, 26, 0.15));
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 8px;
  text-align: center;
`;

const SocialProofText = styled.span<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 13px;
  font-weight: 600;
  
  .count {
    color: #4caf50;
    font-weight: 700;
  }
`;

const PriceRange = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 11px;
  opacity: 0.7;
  text-align: center;
  margin-bottom: 8px;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PriceWrapper = styled.div<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  background: ${p => p.$theme.background};
  border: 1px solid ${p => p.$theme.border};
  border-radius: 6px;
  overflow: hidden;
  flex: 1;
  
  &:focus-within {
    border-color: ${p => p.$theme.primary};
  }
`;

const Prefix = styled.span<{ $theme: Theme }>`
  padding: 8px 10px;
  color: ${p => p.$theme.text};
  font-weight: 600;
  font-size: 16px;
  background: ${p => p.$theme.surface};
`;

const Input = styled.input<{ $theme: Theme }>`
  border: none;
  background: transparent;
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.$theme.primary};
  width: 100px;
  outline: none;
  
  &::placeholder {
    color: ${p => p.$theme.text};
    opacity: 0.3;
  }
`;

const SubmitBtn = styled.button<{ $theme: Theme; $disabled?: boolean }>`
  background: ${p => p.$disabled ? p.$theme.border : p.$theme.primary};
  color: ${p => p.$disabled ? p.$theme.text : '#000'};
  border: none;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    transform: scale(1.02);
  }
`;

const UserBadge = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.primary};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const SuccessContent = styled.div<{ $theme: Theme }>`
  text-align: center;
  padding: 4px 0;
`;

const SuccessPrice = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.primary};
  font-size: 26px;
  font-weight: 700;
`;

const SuccessText = styled.div`
  color: #4caf50;
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
`;

const EmailSection = styled.div<{ $theme: Theme }>`
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid ${p => p.$theme.border};
`;

const EmailLabel = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 9px;
  opacity: 0.7;
  margin-bottom: 4px;
`;

const EmailInput = styled.input<{ $theme: Theme }>`
  width: 100%;
  border: 1px solid ${p => p.$theme.border};
  background: ${p => p.$theme.background};
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 11px;
  color: ${p => p.$theme.text};
  outline: none;
  box-sizing: border-box;
  
  &:focus {
    border-color: ${p => p.$theme.primary};
  }
  
  &::placeholder {
    opacity: 0.4;
  }
`;

const EmailSubmitBtn = styled.button<{ $theme: Theme }>`
  width: 100%;
  margin-top: 6px;
  padding: 6px;
  background: ${p => p.$theme.primary};
  color: #000;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmailSaved = styled.div<{ $theme: Theme }>`
  color: #4caf50;
  font-size: 9px;
  margin-top: 6px;
  text-align: center;
`;

const ShareBtn = styled.a<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #000;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  margin-top: 8px;
  flex: 1;
  
  &:hover {
    border-color: ${p => p.$theme.primary};
    background: #1a1a1a;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ShareActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

const DownloadBtn = styled.button<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${p => p.$theme.text};
  border: 1px solid ${p => p.$theme.border};
  padding: 8px;
  border-radius: 6px;
  font-size: 10px;
  cursor: pointer;
  
  &:hover {
    border-color: ${p => p.$theme.primary};
    color: ${p => p.$theme.primary};
  }
`;

const CopyBtn = styled.button<{ $theme: Theme; $copied?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$copied ? '#4caf50' : 'transparent'};
  color: ${p => p.$copied ? '#fff' : p.$theme.text};
  border: 1px solid ${p => p.$copied ? '#4caf50' : p.$theme.border};
  padding: 8px;
  border-radius: 6px;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${p => p.$theme.primary};
    color: ${p => p.$copied ? '#fff' : p.$theme.primary};
  }
`;

const LoginBtn = styled.button<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #000;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  
  &:hover {
    border-color: ${p => p.$theme.primary};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const DragHint = styled.div<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 9px;
  opacity: 0.4;
  text-align: center;
  margin-top: 6px;
`;

interface Props {
  theme: Theme;
  isLoggedIn: boolean;
  userHandle?: string;
  onLogin: () => void;
  onEntrySubmitted?: (price: number, entry?: { id: number; price_prediction: number; submitted_at: string }) => void;
}

export const ContestPredictionInput: React.FC<Props> = ({
  theme,
  isLoggedIn,
  userHandle,
  onLogin,
  onEntrySubmitted
}) => {
  // Position state - left-center area of chart
  const [position, setPosition] = useState({ x: 300, y: 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [priceInput, setPriceInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEntry, setUserEntry] = useState<{ price_prediction: number } | null>(null);
  const [contest, setContest] = useState<{ 
    id: string; 
    is_open: boolean; 
    entry_count?: number;
    min_price?: number;
    max_price?: number;
  } | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  
  // Email collection state
  const [email, setEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate share URL
  const rawTweetText = userEntry ? 
    `🎯 My #Bitcoin price prediction for #BitcoinConference 2026 (April 28):\n\n$${userEntry.price_prediction.toLocaleString()}\n\n🏆 Win 1.000.000 sats! Make yours:\nhttps://proofofprice.com/btc`
    : '';
  const tweetText = encodeURIComponent(rawTweetText);
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;

  // Copy tweet text to clipboard
  const handleCopyTweet = async () => {
    try {
      await navigator.clipboard.writeText(rawTweetText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Fetch contest
  useEffect(() => {
    fetch('/api/contest/active')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.contest) {
          setContest(d.contest);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch user entry
  useEffect(() => {
    if (isLoggedIn && contest) {
      const token = localStorage.getItem('authToken');
      fetch(`/api/contest/${contest.id}/my-entry`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.entry) {
            setUserEntry(d.entry);
          }
        })
        .catch(() => {});
    }
  }, [isLoggedIn, contest]);

  // Drag handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start drag if clicking on input, button, etc.
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON') return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: Math.max(0, e.clientX - dragOffset.x),
      y: Math.max(0, e.clientY - dragOffset.y)
    });
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSubmit = async () => {
    if (!contest || !priceInput) return;
    const price = parseFloat(priceInput.replace(/,/g, ''));
    if (isNaN(price) || price < 1000) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const r = await fetch(`/api/contest/${contest.id}/enter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ price_prediction: price })
      });
      const d = await r.json();
      if (d.success) {
        const newEntry = { price_prediction: price, id: d.entry?.id, submitted_at: d.entry?.submitted_at || new Date().toISOString() };
        setUserEntry(newEntry);
        setJustSubmitted(true);
        // Notify parent component
        if (onEntrySubmitted) onEntrySubmitted(price, newEntry);
      } else if (r.status === 400) {
        alert(d.error || 'Failed to submit');
        if (d.error?.includes('already')) {
          // User already entered - fetch their existing entry
          const entryRes = await fetch(`/api/contest/${contest.id}/my-entry`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const entryData = await entryRes.json();
          if (entryData.success && entryData.entry) {
            setUserEntry(entryData.entry);
            // Notify parent about existing entry
            if (onEntrySubmitted) onEntrySubmitted(entryData.entry.price_prediction, entryData.entry);
          }
        }
      }
    } catch (err) {
      console.error('Contest entry error:', err);
      alert('Failed to submit prediction');
    } finally { setIsSubmitting(false); }
  };
  
  const handleEmailSubmit = async () => {
    if (!email || !email.includes('@') || !contest) return;
    
    setEmailSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      // Update the entry with email
      await fetch(`/api/contest/${contest.id}/update-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email, wants_notifications: true })
      });
      setEmailSaved(true);
    } catch {}
    finally { setEmailSubmitting(false); }
  };

  // Download share image
  const handleDownloadImage = async () => {
    if (!contest || !userHandle) return;
    
    try {
      const response = await fetch(`/api/share-image/contest/${contest.id}/${userHandle}`);
      if (!response.ok) throw new Error('Failed to generate image');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bitcoin-prediction-${userHandle}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const formatNumber = (n: string) => {
    // First, check if user just typed a decimal separator (comma or period at end)
    const endsWithDecimal = n.endsWith(',') || n.endsWith('.');
    
    // Remove all existing thousand-separator commas and keep only digits and one decimal point
    const stripped = n.replace(/,/g, ''); // Remove thousand separators
    const clean = stripped.replace(/[^0-9.]/g, '').replace(/(\.)(?=.*\.)/g, ''); // Keep only digits and first decimal
    
    if (!clean) return '';
    
    const parts = clean.split('.');
    const intPart = parts[0] ? parseInt(parts[0]).toLocaleString('en-US') : '0';
    
    // If user just typed a decimal separator, add it
    if (endsWithDecimal && parts.length === 1) {
      return intPart + '.';
    }
    
    if (parts.length > 1) {
      return `${intPart}.${parts[1].slice(0, 2)}`; // Max 2 decimal places
    }
    return intPart;
  };

  if (!isVisible || !contest?.is_open) return null;

  return (
    <Container
      ref={containerRef}
      $x={position.x}
      $y={position.y}
      $theme={theme}
      $isDragging={isDragging}
      onMouseDown={handleMouseDown}
    >
      <Card $theme={theme}>
        <Header $theme={theme}>
          <Title $theme={theme}>🎯 BTC price prediction contest</Title>
          <CloseBtn $theme={theme} onClick={() => setIsVisible(false)}>×</CloseBtn>
        </Header>
        
        {userEntry ? (
          <SuccessContent $theme={theme}>
            <SuccessPrice $theme={theme}>${userEntry.price_prediction.toLocaleString()}</SuccessPrice>
            <SuccessText>{justSubmitted ? '🎉 Entry locked!' : '✅ Locked in!'}</SuccessText>
            <ShareActions>
              <ShareBtn 
                $theme={theme} 
                href={tweetUrl} 
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Share
              </ShareBtn>
              <DownloadBtn
                $theme={theme}
                onClick={(e) => { e.stopPropagation(); handleDownloadImage(); }}
                title="Download share image"
              >
                📷
              </DownloadBtn>
              <CopyBtn
                $theme={theme}
                $copied={copied}
                onClick={(e) => { e.stopPropagation(); handleCopyTweet(); }}
                title="Copy tweet text"
              >
                {copied ? '✓' : '📋'}
              </CopyBtn>
            </ShareActions>
            {/* Email collection for notifications */}
            {!emailSaved && (
              <EmailSection $theme={theme}>
                <EmailLabel $theme={theme}>📧 Get notified when results are in:</EmailLabel>
                <EmailInput
                  $theme={theme}
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <EmailSubmitBtn
                  $theme={theme}
                  disabled={!email.includes('@') || emailSubmitting}
                  onClick={(e) => { e.stopPropagation(); handleEmailSubmit(); }}
                >
                  {emailSubmitting ? '...' : 'Notify me'}
                </EmailSubmitBtn>
              </EmailSection>
            )}
            {emailSaved && (
              <EmailSaved $theme={theme}>✓ We'll notify you!</EmailSaved>
            )}
          </SuccessContent>
        ) : isLoggedIn ? (
          <>
            <SocialProof $theme={theme}>
              <SocialProofText $theme={theme}>
                {(contest?.entry_count ?? 0) > 0 
                  ? <>🔥 Join <span className="count">{contest!.entry_count}</span> {contest!.entry_count === 1 ? 'predictor' : 'predictors'}!</>
                  : <>✨ Be first!</>
                }
              </SocialProofText>
            </SocialProof>
            <Subtitle $theme={theme}>April 28, 4PM PT - Binance price</Subtitle>
            {contest && contest.min_price && contest.max_price && (
              <PriceRange $theme={theme}>
                Range: ${(contest.min_price / 1000).toFixed(0)}k - ${(contest.max_price / 1000).toFixed(0)}k
              </PriceRange>
            )}
            <UserBadge $theme={theme}>@{userHandle}</UserBadge>
            <InputRow>
              <PriceWrapper $theme={theme}>
                <Prefix $theme={theme}>$</Prefix>
                <Input
                  $theme={theme}
                  type="text"
                  inputMode="decimal"
                  placeholder="95,000.00"
                  value={priceInput}
                  onChange={(e) => setPriceInput(formatNumber(e.target.value))}
                  maxLength={12}
                  onClick={(e) => e.stopPropagation()}
                />
              </PriceWrapper>
              <SubmitBtn
                $theme={theme}
                $disabled={!priceInput || isSubmitting}
                onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
                disabled={!priceInput || isSubmitting}
              >
                {isSubmitting ? '...' : '🔒'}
              </SubmitBtn>
            </InputRow>
          </>
        ) : (
          <>
            <SocialProof $theme={theme}>
              <SocialProofText $theme={theme}>
                {(contest?.entry_count ?? 0) > 0 
                  ? <>🔥 Join <span className="count">{contest!.entry_count}</span> {contest!.entry_count === 1 ? 'predictor' : 'predictors'}!</>
                  : <>✨ Be first!</>
                }
              </SocialProofText>
            </SocialProof>
            <Subtitle $theme={theme}>Win 1.000.000 sats!</Subtitle>
            {contest && contest.min_price && contest.max_price && (
              <PriceRange $theme={theme}>
                Predictions: ${(contest.min_price / 1000).toFixed(0)}k - ${(contest.max_price / 1000).toFixed(0)}k
              </PriceRange>
            )}
            <LoginBtn $theme={theme} onClick={(e) => { e.stopPropagation(); onLogin(); }}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Sign in with X
            </LoginBtn>
          </>
        )}
        <DragHint $theme={theme}>drag to move</DragHint>
      </Card>
    </Container>
  );
};

export default ContestPredictionInput;
